import { ProductLine, SourceType } from '../helpers/home_services.helper';
import { createUser } from '../services/api';
import { v4 as uuidv4 } from 'uuid';
import { CreateUserData } from './schemas/user.request';
import {
  SessionStorageKey,
  updateSessionObjectStorageItem,
  UserSessionData,
} from '../storage/session_storage';

enum SourceName {
  Gclid = 'gclid',
  Gbraid = 'gbraid',
  Wbraid = 'wbraid',
  Fbclid = 'fbclid',
  Msclkid = 'msclkid',
}

const UTM_CAMPAIGN = 'utm_campaign';
const GOOGLE_CAMPAIGN_ID = 'campaignid';
const FACEBOOK_ADGROUP_ID = 'adgroupid';
const UTM_SOURCE = 'utm_source';

const SOURCE_NAME_TO_TYPES: Partial<{ [key in SourceType]: SourceName[] }> = {
  // Order importand for priority
  [SourceType.Google]: [SourceName.Gclid, SourceName.Gbraid, SourceName.Wbraid],
  [SourceType.Facebook]: [SourceName.Fbclid],
  [SourceType.Bing]: [SourceName.Msclkid],
};

async function getUserInitioalData(
  extractGeoData = true,
  userPublicId?: string
): Promise<CreateUserData> {
  const hostUrl = window.location.href;
  const params = new URLSearchParams(window.location.search);

  const landingProductLine = getProductByString(
    window.location.pathname?.split('/')[1]
  );
  let geoData = undefined;
  if (extractGeoData === true && typeof window !== 'undefined') {
    try {
      geoData = await fetch('/api/geo').then(res => res.json());
    } catch (error) {
      console.error('Error fetching geo data', error);
    }
  }
  const keyword = params.get('keyword') ?? params.get('utm_term') ?? undefined;
  const campaignId = params.get(GOOGLE_CAMPAIGN_ID) ?? params.get(UTM_CAMPAIGN);
  const adGroupId = params.get(FACEBOOK_ADGROUP_ID);

  const { sourceId, sourceName, sourceType } = getUserMediaIdentifier(params);
  const originHost = hostUrl ? new URL(hostUrl).hostname : '';

  let publicId;
  try {
    publicId = userPublicId ?? window.crypto.randomUUID();
  } catch (error) {
    publicId = userPublicId ?? uuidv4();
  }

  return {
    publicId,
    geoData,
    keyword,
    originHost,
    landingProductLine,
    ...(sourceId != null && { sourceId }),
    ...(sourceType != null && { sourceType }),
    ...(sourceName != null && { sourceName }),
    ...(campaignId != null && { campaignId }),
    ...(adGroupId != null && { adGroupId }),
  };
}

export function getUserMediaIdentifier(params: URLSearchParams): {
  sourceType?: SourceType;
  sourceId?: string;
  sourceName?: SourceName;
} {
  let sourceId: undefined | string;
  let sourceName: undefined | SourceName;
  let sourceType: undefined | SourceType;

  const utmSource = params.get(UTM_SOURCE);
  if (utmSource === SourceType.Youtube) {
    sourceType = SourceType.Youtube;
  }

  for (const [sourceTypeById, sourceNames] of Object.entries(
    SOURCE_NAME_TO_TYPES
  )) {
    for (const sourceName of sourceNames) {
      const sourceId = params.get(sourceName);
      if (sourceId != null) {
        const realSourceId = sourceId !== '{gclid}' ? sourceId : undefined; // {gclid} come from bots

        return {
          sourceId: realSourceId,
          sourceName,
          sourceType: sourceType ?? (sourceTypeById as SourceType),
        };
      }
    }
  }
  if (sourceType == null) {
    const utmSource = params.get(UTM_SOURCE);
    if (utmSource != null) {
      switch (utmSource) {
        case 'google':
          sourceType = SourceType.Google;
          break;
        case 'fb':
          sourceType = SourceType.Facebook;
          break;
        default:
          break;
      }
    }
  }

  return { sourceId, sourceName, sourceType };
}

export async function createUserOnPageLoading(): Promise<CreateUserData> {
  const userInitialData = await getUserInitioalData();
  createUser(userInitialData);
  updateUserData(userInitialData);
  return userInitialData;
}

export async function getUserOnPageLoading(
  userPublicId: string
): Promise<CreateUserData> {
  const userInitialData = await getUserInitioalData(false, userPublicId);
  updateUserData(userInitialData);
  return userInitialData;
}

function updateUserData(userInitialData: CreateUserData): void {
  updateSessionObjectStorageItem<UserSessionData>(SessionStorageKey.userData, {
    publicId: userInitialData.publicId,
    sourceId: userInitialData.sourceId,
    sourceName: userInitialData.sourceName,
    campaignId: userInitialData.campaignId,
    sourceType: userInitialData.sourceType,
    zipCodeByIp: userInitialData.geoData?.zipCode,
  });
}

export function getProductByString(product: string): ProductLine {
  if (product == null) {
    return ProductLine.Windows;
  }

  switch (product) {
    case ProductLine.Windows:
      return ProductLine.Windows;
    default:
      // Sentry
      return ProductLine.Windows;
  }
}
